import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Building2, Plus, Eye, MessageSquare, Calendar,
  TrendingUp, CheckCircle2, Clock, AlertTriangle, ChevronRight,
  FileText, Upload, Shield, BarChart3, Users, Home, Bell,
  ChevronLeft, LogOut
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import api from '@/services/api';
import AEDIcon from '@/components/icons/AEDIcon';

// Status badge colors
const STATUS_COLORS = {
  draft: 'bg-gray-600/20 text-gray-400',
  pending_review: 'bg-gold-600/20 text-gold-400',
  active: 'bg-green-600/20 text-green-400',
  in_viewing_cycle: 'bg-blue-600/20 text-blue-400',
  under_offer: 'bg-purple-600/20 text-purple-400',
  contracted: 'bg-emerald-600/20 text-emerald-400',
  settled: 'bg-green-700/20 text-green-500',
  withdrawn: 'bg-red-600/20 text-red-400'
};

const VIEWING_STATUS_COLORS = {
  pending: 'bg-gold-600/20 text-gold-400',
  approved: 'bg-green-600/20 text-green-400',
  rejected: 'bg-red-600/20 text-red-400',
  completed: 'bg-blue-600/20 text-blue-400',
  cancelled: 'bg-gray-600/20 text-gray-400'
};

export const SellerDashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [properties, setProperties] = useState([]);
  const [viewings, setViewings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    fetchSellerData();
  }, []);

  const fetchSellerData = async () => {
    try {
      setLoading(true);
      const [profileRes, propertiesRes, viewingsRes, offersRes, analyticsRes, metricsRes] = await Promise.all([
        api.get('/seller/profile'),
        api.get('/seller/properties'),
        api.get('/seller/viewings').catch(() => ({ data: { viewings: [] } })),
        api.get('/seller/offers').catch(() => ({ data: { offers: [] } })),
        api.get('/seller/analytics').catch(() => ({ data: null })),
        api.get('/seller/dashboard/metrics').catch(() => ({ data: null }))
      ]);

      setSellerProfile(profileRes.data);
      setProperties(propertiesRes.data.properties || []);
      setViewings(viewingsRes.data.viewings || []);
      setOffers(offersRes.data.offers || []);
      // Merge enhanced metrics with analytics
      const enhancedAnalytics = {
        ...analyticsRes.data,
        enhanced: metricsRes.data
      };
      setAnalytics(enhancedAnalytics);
    } catch (error) {
      console.error('Failed to fetch seller data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price >= 1000000) return `AED ${(price / 1000000).toFixed(1)}M`;
    return `AED ${price?.toLocaleString()}`;
  };

  // Check if seller needs to complete onboarding
  const needsOnboarding = sellerProfile && (
    sellerProfile.seller_status === 'pending_verification' ||
    sellerProfile.kyc_status === 'pending' ||
    !sellerProfile.agreements_accepted
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="seller-dashboard">
      {/* Header */}
      <header className="border-b border-white/10 bg-secondary/20 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Back Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            
            {/* Home Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-muted-foreground hover:text-foreground"
              data-testid="home-btn"
            >
              <Home className="w-4 h-4 mr-1" />
              Home
            </Button>
            
            <Separator orientation="vertical" className="h-6 bg-white/10" />
            
            {/* Rashii Logo - Consistent with Landing Page */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 rounded logo-icon-metallic flex items-center justify-center">
                <span className="font-display text-lg text-primary-foreground font-bold">R</span>
              </div>
              <span className="font-display text-xl font-medium tracking-tight gold-interactive">Rashii</span>
            </div>
            
            <Separator orientation="vertical" className="h-6 bg-white/10" />
            
            {/* Dashboard Title */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-primary/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-xl">Seller Dashboard</h1>
                <p className="text-xs text-muted-foreground">
                  {sellerProfile?.full_name || user?.username}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* KYC Status Badge - Clickable with Rashii Effect */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (sellerProfile?.kyc_status === 'pending' || sellerProfile?.seller_status === 'pending_verification') {
                  navigate('/seller/onboarding');
                } else {
                  toast.info('KYC Status: ' + (sellerProfile?.kyc_status || 'pending'));
                }
              }}
              className="p-0"
              data-testid="kyc-status-btn"
            >
              <Badge 
                variant={sellerProfile?.seller_status === 'active' ? 'default' : 'gold'}
                className={sellerProfile?.seller_status === 'active' ? 'bg-green-600/20 text-green-400 cursor-pointer' : 'cursor-pointer'}
              >
                {sellerProfile?.kyc_status === 'pending' ? 'KYC Pending' : sellerProfile?.seller_status?.replace('_', ' ')}
              </Badge>
            </Button>
            
            {/* Notifications */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => toast.info('Notifications coming soon')}
              className="relative"
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
            </Button>
            
            {/* Add Property */}
            <Button onClick={() => navigate('/seller/property/create')} className="btn-gold-metallic" data-testid="add-property-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Property
            </Button>
            
            {/* Logout */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-foreground"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Onboarding Banner - RED for urgency (#10) */}
        {needsOnboarding && (
          <Card className="glass-card border-red-600/30 mb-6" data-testid="onboarding-banner">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 rounded bg-red-600/20">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="font-medium text-red-400">Complete Your Verification</p>
                  <p className="text-sm text-muted-foreground">
                    {sellerProfile?.kyc_status === 'pending' && 'Submit your identity documents'}
                    {sellerProfile?.kyc_status === 'under_review' && 'KYC under review - Accept platform agreements'}
                    {!sellerProfile?.agreements_accepted && sellerProfile?.kyc_status !== 'pending' && 'Accept Rashii settlement protocol'}
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/seller/onboarding')} 
                className="btn-gold-metallic"
                data-testid="complete-now-btn"
              >
                Complete Now <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid - Enhanced with Viewing Slots */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <Card className="glass-card border-white/10">
            <CardContent className="p-4 text-center">
              <Building2 className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-2xl font-display text-primary">{properties.length}</p>
              <p className="text-xs text-muted-foreground">Total Properties</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-white/10">
            <CardContent className="p-4 text-center">
              <Eye className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-display text-blue-400">
                {analytics?.enhanced?.summary?.viewing_slots_remaining || (properties.length * 9)}
              </p>
              <p className="text-xs text-muted-foreground">Viewing Slots Left</p>
              <p className="text-[10px] text-muted-foreground/60">
                of {analytics?.enhanced?.summary?.total_viewing_slots || (properties.length * 9)}
              </p>
            </CardContent>
          </Card>
          <Card className="glass-card border-white/10">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-gold-400 mx-auto mb-2" />
              <p className="text-2xl font-display text-gold-400">
                {analytics?.enhanced?.summary?.times_selected || 0}
              </p>
              <p className="text-xs text-muted-foreground">Times Selected</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-white/10">
            <CardContent className="p-4 text-center">
              <AEDIcon className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-display text-green-400">{offers.filter(o => o.status === 'pending').length}</p>
              <p className="text-xs text-muted-foreground">Pending Offers</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-white/10">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-display text-purple-400">
                {analytics?.enhanced?.summary?.conversion_rate || '0%'}
              </p>
              <p className="text-xs text-muted-foreground">Conversion Rate</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-secondary/30">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="listings">My Listings</TabsTrigger>
            <TabsTrigger value="viewings">Viewings</TabsTrigger>
            <TabsTrigger value="offers">Offers</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Recent Listings */}
              <Card className="glass-card border-white/10">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5" /> Recent Listings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {properties.slice(0, 3).map((prop) => (
                    <div 
                      key={prop.id} 
                      className="flex items-center justify-between p-3 rounded bg-secondary/20 cursor-pointer hover:bg-secondary/30 transition-colors"
                      onClick={() => navigate(`/seller/property/${prop.id}`)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm truncate max-w-[200px]">{prop.title}</p>
                          <p className="text-xs text-muted-foreground">{formatPrice(prop.price)}</p>
                        </div>
                      </div>
                      <Badge className={STATUS_COLORS[prop.status] || STATUS_COLORS.draft}>
                        {prop.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                  ))}
                  {properties.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No properties yet</p>
                      <Button size="sm" className="mt-3" onClick={() => navigate('/seller/property/create')}>
                        <Plus className="w-4 h-4 mr-1" /> Add Property
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pending Actions */}
              <Card className="glass-card border-white/10">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5" /> Pending Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Verification Actions */}
                  {needsOnboarding && (
                    <div 
                      className="p-3 rounded bg-amber-600/10 border-l-2 border-gold-500 cursor-pointer hover:bg-amber-600/20 transition-colors"
                      onClick={() => navigate('/seller/onboarding')}
                      data-testid="verification-action-card"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium">Complete Verification</p>
                        <Badge variant="gold">Required</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {sellerProfile?.kyc_status === 'pending' && 'Submit your identity documents'}
                        {sellerProfile?.kyc_status === 'under_review' && 'KYC under review - Accept platform agreements'}
                        {!sellerProfile?.agreements_accepted && sellerProfile?.kyc_status !== 'pending' && sellerProfile?.kyc_status !== 'under_review' && 'Accept Rashii settlement protocol'}
                      </p>
                    </div>
                  )}
                  {viewings.filter(v => v.status === 'pending').slice(0, 2).map((viewing) => (
                    <div key={viewing.id} className="p-3 rounded bg-secondary/20 border-l-2 border-gold-500">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium">Viewing Request</p>
                        <Badge className={VIEWING_STATUS_COLORS.pending}>Pending</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">{viewing.property_title}</p>
                      <p className="text-xs text-muted-foreground">
                        {viewing.preferred_date} at {viewing.preferred_time}
                      </p>
                    </div>
                  ))}
                  {offers.filter(o => o.status === 'pending').slice(0, 2).map((offer) => (
                    <div key={offer.id} className="p-3 rounded bg-secondary/20 border-l-2 border-green-500">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium">New Offer</p>
                        <Badge className="bg-green-600/20 text-green-400">{formatPrice(offer.offer_amount)}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">{offer.property_title}</p>
                      <p className="text-xs text-muted-foreground">
                        {offer.offer_vs_asking_pct > 0 ? '+' : ''}{offer.offer_vs_asking_pct}% vs asking
                      </p>
                    </div>
                  ))}
                  {!needsOnboarding && viewings.filter(v => v.status === 'pending').length === 0 && offers.filter(o => o.status === 'pending').length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No pending actions</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">My Listings ({properties.length})</h3>
              <Button onClick={() => navigate('/seller/property/create')}>
                <Plus className="w-4 h-4 mr-2" /> Add Property
              </Button>
            </div>
            <div className="grid gap-4">
              {properties.map((prop) => (
                <Card key={prop.id} className="glass-card border-white/10 hover:border-primary/30 transition-colors cursor-pointer" onClick={() => navigate(`/seller/property/${prop.id}`)}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-20 h-20 rounded bg-secondary/50 flex items-center justify-center flex-shrink-0">
                      {prop.images?.[0] ? (
                        <img src={prop.images[0]} alt={prop.title} className="w-full h-full object-cover rounded" />
                      ) : (
                        <Building2 className="w-8 h-8 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium truncate">{prop.title}</h4>
                        <Badge className={STATUS_COLORS[prop.status] || STATUS_COLORS.draft}>
                          {prop.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{prop.community}, {prop.city}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{prop.bedrooms} bed • {prop.bathrooms} bath • {prop.area_sqft?.toLocaleString()} sqft</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg font-display text-primary">{formatPrice(prop.price)}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
                        <span className="flex items-center gap-1 text-blue-400" title="Viewing Slots">
                          <Eye className="w-3 h-3" /> {prop.viewing_slots_remaining ?? 9}/{prop.viewing_slots_total ?? 9}
                        </span>
                        <span className="flex items-center gap-1" title="Times Selected">
                          <Users className="w-3 h-3" /> {prop.selection_count || 0}
                        </span>
                        <span className="flex items-center gap-1" title="Offers">
                          <AEDIcon className="w-3 h-3" /> {prop.offers_received || 0}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </CardContent>
                </Card>
              ))}
              {properties.length === 0 && (
                <Card className="glass-card border-white/10">
                  <CardContent className="p-12 text-center">
                    <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium mb-2">No properties listed yet</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create your first property listing to start receiving viewings and offers
                    </p>
                    <Button onClick={() => navigate('/seller/property/create')}>
                      <Plus className="w-4 h-4 mr-2" /> Add Your First Property
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Viewings Tab */}
          <TabsContent value="viewings" className="space-y-4">
            <h3 className="text-lg font-medium mb-4">Viewing Requests ({viewings.length})</h3>
            <div className="grid gap-4">
              {viewings.map((viewing) => (
                <Card key={viewing.id} className="glass-card border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{viewing.buyer_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {viewing.buyer_kyc_status === 'verified' ? '✓ KYC Verified' : 'KYC Pending'}
                            {viewing.buyer_funds_verified && ' • ✓ Funds Verified'}
                          </p>
                        </div>
                      </div>
                      <Badge className={VIEWING_STATUS_COLORS[viewing.status] || VIEWING_STATUS_COLORS.pending}>
                        {viewing.status}
                      </Badge>
                    </div>
                    <Separator className="my-3" />
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Property</p>
                        <p className="font-medium">{viewing.property_title}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground mb-1">Requested</p>
                        <p className="font-medium">{viewing.preferred_date} at {viewing.preferred_time}</p>
                      </div>
                    </div>
                    {viewing.status === 'pending' && (
                      <div className="flex gap-2 mt-4">
                        <Button size="sm" className="flex-1" onClick={() => handleViewingDecision(viewing.id, true)}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => handleViewingDecision(viewing.id, false)}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {viewings.length === 0 && (
                <Card className="glass-card border-white/10">
                  <CardContent className="p-12 text-center">
                    <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium mb-2">No viewing requests yet</p>
                    <p className="text-sm text-muted-foreground">
                      Viewing requests from verified buyers will appear here
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers" className="space-y-4">
            <h3 className="text-lg font-medium mb-4">Offers Received ({offers.length})</h3>
            <div className="grid gap-4">
              {offers.map((offer) => (
                <Card key={offer.id} className="glass-card border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium">{offer.property_title}</p>
                        <p className="text-xs text-muted-foreground">From: {offer.buyer_name}</p>
                      </div>
                      <Badge className={
                        offer.status === 'accepted' ? 'bg-green-600/20 text-green-400' :
                        offer.status === 'countered' ? 'bg-blue-600/20 text-blue-400' :
                        offer.status === 'rejected' ? 'bg-red-600/20 text-red-400' :
                        'bg-gold-600/20 text-gold-400'
                      }>
                        {offer.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div className="p-3 rounded bg-secondary/20 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Offer Amount</p>
                        <p className="text-lg font-display text-primary">{formatPrice(offer.offer_amount)}</p>
                      </div>
                      <div className="p-3 rounded bg-secondary/20 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Asking Price</p>
                        <p className="text-lg font-display">{formatPrice(offer.asking_price)}</p>
                      </div>
                      <div className="p-3 rounded bg-secondary/20 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Difference</p>
                        <p className={`text-lg font-display ${offer.offer_vs_asking_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {offer.offer_vs_asking_pct > 0 ? '+' : ''}{offer.offer_vs_asking_pct}%
                        </p>
                      </div>
                    </div>
                    {offer.price_elasticity_range && (
                      <div className="p-3 rounded bg-primary/5 border border-primary/20 mb-3">
                        <p className="text-xs text-primary mb-1">Rashii Elasticity Guidance</p>
                        <p className="text-sm text-muted-foreground">
                          Negotiation bandwidth: {offer.price_elasticity_range.elasticity_min}-{offer.price_elasticity_range.elasticity_max}% below asking
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Recommended: <span className="text-primary font-medium">{offer.recommended_action}</span>
                        </p>
                      </div>
                    )}
                    {offer.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button size="sm" className="flex-1" onClick={() => handleOfferDecision(offer.id, 'accept')}>
                          Accept
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => handleOfferDecision(offer.id, 'counter')}>
                          Counter
                        </Button>
                        <Button size="sm" variant="destructive" className="flex-1" onClick={() => handleOfferDecision(offer.id, 'reject')}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {offers.length === 0 && (
                <Card className="glass-card border-white/10">
                  <CardContent className="p-12 text-center">
                    <AEDIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium mb-2">No offers received yet</p>
                    <p className="text-sm text-muted-foreground">
                      Offers from verified buyers will appear here
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Analytics Tab - Enhanced with Viewing Slots */}
          <TabsContent value="analytics" className="space-y-4">
            <h3 className="text-lg font-medium mb-4">Performance Analytics</h3>
            {analytics && (
              <div className="grid grid-cols-2 gap-6">
                {/* Summary Card */}
                <Card className="glass-card border-white/10">
                  <CardHeader>
                    <CardTitle className="text-lg">Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Total Properties</span>
                      <span className="font-display text-lg">{analytics.enhanced?.total_properties || analytics.summary?.total_properties || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Viewing Slots Remaining</span>
                      <span className="font-display text-lg text-blue-400">
                        {analytics.enhanced?.summary?.viewing_slots_remaining || 0} / {analytics.enhanced?.summary?.total_viewing_slots || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Times Selected (Rounds)</span>
                      <span className="font-display text-lg text-gold-400">{analytics.enhanced?.summary?.times_selected || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Viewings Completed</span>
                      <span className="font-display text-lg">{analytics.enhanced?.summary?.viewings_completed || analytics.summary?.total_viewings || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Total Offers</span>
                      <span className="font-display text-lg">{analytics.enhanced?.summary?.total_offers || analytics.summary?.total_offers || 0}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Conversion Rate</span>
                      <span className="font-display text-lg text-primary">{analytics.enhanced?.summary?.conversion_rate || '0%'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Avg Offer vs Asking</span>
                      <span className="font-display text-lg text-primary">{analytics.enhanced?.summary?.average_offer_vs_asking || '0%'}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Property Performance with Viewing Slots */}
                <Card className="glass-card border-white/10">
                  <CardHeader>
                    <CardTitle className="text-lg">Property Performance</CardTitle>
                    <p className="text-xs text-muted-foreground">{analytics.enhanced?.zone_confidence_tip}</p>
                  </CardHeader>
                  <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
                    {(analytics.enhanced?.properties || analytics.properties)?.map((prop) => (
                      <div key={prop.property_id || prop.id} className="p-3 rounded bg-secondary/20">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-sm truncate max-w-[180px]">{prop.title}</p>
                          <Badge className={STATUS_COLORS[prop.status] || STATUS_COLORS.draft} variant="outline">
                            {prop.status?.replace('_', ' ') || 'draft'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-5 gap-1 text-xs">
                          <div className="text-center p-1 rounded bg-blue-600/10">
                            <p className="text-muted-foreground">Slots</p>
                            <p className="font-medium text-blue-400">
                              {prop.viewing_slots_remaining || 9}/{prop.viewing_slots_total || 9}
                            </p>
                          </div>
                          <div className="text-center p-1 rounded bg-gold-600/10">
                            <p className="text-muted-foreground">Selected</p>
                            <p className="font-medium text-gold-400">{prop.times_selected || 0}</p>
                          </div>
                          <div className="text-center p-1 rounded bg-purple-600/10">
                            <p className="text-muted-foreground">Viewings</p>
                            <p className="font-medium text-purple-400">{prop.viewings_completed || prop.viewing_requests || 0}</p>
                          </div>
                          <div className="text-center p-1 rounded bg-green-600/10">
                            <p className="text-muted-foreground">Offers</p>
                            <p className="font-medium text-green-400">{prop.offers_received || prop.offers || 0}</p>
                          </div>
                          <div className="text-center p-1 rounded bg-primary/10">
                            <p className="text-muted-foreground">Price</p>
                            <p className={`font-medium ${prop.price_positioning_vs_benchmark?.startsWith('-') ? 'text-green-400' : 'text-orange-400'}`}>
                              {prop.price_positioning_vs_benchmark || '0%'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );

  async function handleViewingDecision(viewingId, approved) {
    try {
      await api.post(`/seller/viewings/${viewingId}/respond`, {
        approved,
        confirmed_date: approved ? new Date().toISOString().split('T')[0] : null,
        confirmed_time: approved ? '10:00' : null,
        rejection_reason: approved ? null : 'Not available'
      });
      fetchSellerData();
    } catch (error) {
      console.error('Failed to respond to viewing:', error);
    }
  }

  async function handleOfferDecision(offerId, action) {
    try {
      await api.post(`/seller/offers/${offerId}/respond`, null, {
        params: { action }
      });
      fetchSellerData();
    } catch (error) {
      console.error('Failed to respond to offer:', error);
    }
  }
};

export default SellerDashboardPage;
